<template>
  <div>
    <CSVOptionsModal
      :handleCancel="handleCSVOptionsCancel"
      :showFileUploadModal="showFileUploadModal"
      :visible="modalData.CSVOptionsModalVisible"
    >
      <ParagraphBody
        v-if="show11thUserWarningText"
        :data-test-id="$testId('csvOptionWarningText')"
      >
        <span>{{ warningText }} </span>
        <Link
          data-test-id="importLearnMoreLink"
          :href="importKbLink"
          isExternal
          target="_blank"
          text="Learn More"
        />
      </ParagraphBody>
    </CSVOptionsModal>
    <FileUploadModal
      ref="fileUploadModal"
      :handleCancel="handleFileUploadCancel"
      :handleMainAction="handleFileUploadMainAction"
      :loading="loading"
      :visible="modalData.fileUploadModalVisible"
      @fileSelected="setUploadedFile"
    >
      <ParagraphBody v-if="show11thUserWarningText">
        <span>{{ warningText }} </span>
        <Link
          data-test-id="billingLearnMoreLink"
          :href="billingKbLink"
          isExternal
          target="_blank"
          text="Learn More"
        />
      </ParagraphBody>
    </FileUploadModal>
    <ImportUsersModal
      :disclaimerText="disclaimerText"
      :handleCancel="handleImportUserModalCancel"
      headerText="Import from CSV"
      importType="csv"
      :itemType="itemType"
      :visible="modalData.importUsersModalVisible"
    >
      <template
        v-slot:selectionComponent="slotProps"
      >
        <ImportUsersSelection v-bind="slotProps" />
      </template>
    </ImportUsersModal>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { Link, ParagraphBody } from '@jumpcloud/ui-components';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import CSVOptionsModal from 'client/systemusers/ImportUsers/CSVOptionsModal.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FileUploadModal from 'client/components/Modal/FileUploadModal.vue';
import ImportUsersModal from 'client/components/ImportUsers/ImportUsersModal.vue';
import ImportUsersSelection from 'client/systemusers/ImportUsers/ImportUsersSelection.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

const settingsModule = Constants.StoreModuleMap[Constants.ItemTypeConsts.settings];

const { ItemTypeConsts } = Constants;
const { error } = Constants.default;
const {
  csvEventTypes,
  exceed11UsersWarning,
  importErrors,
  requiredColumns,
  defaultMaxUsersDisclaimer,
} = Constants.ImportUsersConsts;

const {
  description,
  location,
  section,
  text,
} = eventProperties;

export default {
  name: 'ImportFromCSVModal',

  components: {
    CSVOptionsModal,
    FileUploadModal,
    ImportUsersModal,
    ImportUsersSelection,
    Link,
    ParagraphBody,
  },

  props: {
    handleCancel: {
      default: () => {},
      type: Function,
    },
  },

  data() {
    return {
      disclaimerText: defaultMaxUsersDisclaimer,
      file: null,
      loading: false,
      itemType: ItemTypeConsts.importUsers,
      billingKbLink: KbArticleUrls.billingChargesInvoices,
      importKbLink: KbArticleUrls.importUsersFromCsv,
      modalData: {
        CSVOptionsModalVisible: true,
        fileUploadModalVisible: false,
        importUsersModalVisible: false,
      },
    };
  },

  computed: {
    ...mapGetters(settingsModule, ['isProviderOrg']),

    ...mapState(settingsModule, [
      'isCustomer',
    ]),

    show11thUserWarningText() {
      return !this.isProviderOrg;
    },

    warningText() {
      return this.isCustomer ? exceed11UsersWarning.whileCustomer : exceed11UsersWarning.whileVfa;
    },
  },

  methods: {
    ...mapActions('ImportUsersCollection', {
      populateImportUsers: 'populateImportUsers',
    }),

    handleCSVOptionsCancel() {
      this.modalData.CSVOptionsModalVisible = false;
      this.handleCancel();
    },

    handleFileUploadCancel() {
      this.modalData.fileUploadModalVisible = false;
      this.loading = false;
      this.handleCancel();
    },

    /**
     * Get the uploaded file and parse the contents.
     */
    handleFileUploadMainAction() {
      this.loading = true;

      // Wrap in a timeout to make sure we see the loading spinner
      setTimeout(() => {
        const { file } = this;
        const reader = new FileReader();

        reader.onerror = () => {
          this.modalData.fileUploadModalVisible = false;
          notify(error, importErrors.unableToImportUsers);
          this.loading = false;
        };

        reader.onload = async (e) => {
          await this.parseCsvFile(e.target.result);
          this.modalData.fileUploadModalVisible = false;
          this.loading = false;
        };

        reader.readAsText(file);
      }, 50);
    },

    /**
     * Dismiss the modal.
     */
    handleImportUserModalCancel() {
      this.modalData.importUsersModalVisible = false;
      this.handleCancel();
    },

    /**
     * The `csvtojson` package allows you to catch various event types.
     *
     * Event type `header`: allows us to validate that the required
     * column headers are in the spreadsheet; these headers are case sensitive.
     *
     * Event type `error`: allows us to notify the admin of errors found during
     * import, for example, if the number of columns in a row is not the same as
     * the number of header columns.
     *
     * @see https://www.npmjs.com/package/csvtojson
     *
     * @todo This method could benefit from a re-factor to separate the
     * event handling on the library from the work needed for the module.  The
     * primary benefit would be not passing a status flag between callbacks.
     * See the comments on `https://github.com/TheJumpCloud/jumpcloud-admin-portal/pull/1207`.
     */
    parseCsvFile(result) {
      let isFileValid;

      return new Promise((resolve, reject) => {
        // Lazy load CsvToJson Module
        import('csvtojson').then((module) => module.default({
          checkColumn: true,
        })
          .fromString(result)
          .on(csvEventTypes.header, (header) => {
            isFileValid = requiredColumns.every(col => header.includes(col));
            if (!isFileValid) {
              notify(
                error,
                `${importErrors.missingColumn} Required columns are: ${requiredColumns.join(', ')}`,
              );
            }
          })
          .on(csvEventTypes.error, (err) => {
            if (isFileValid) {
              if (get(err, 'name', '') && get(err, 'message', '')) {
                notify(error, `${err.name} - ${err.message}`);
              } else {
                notify(error, importErrors.noUsersToImport);
              }
              isFileValid = false;
            }
          })
          .then((users) => {
            if (isFileValid) {
              if (users.length) {
                this.populateImportUsers(users);
                this.showImportUserModal();
              } else {
                notify(error, importErrors.noUsersToImport);
              }
            }
            resolve();
          })
          .catch(() => {
            notify(error, importErrors.unableToImportUsers);
            reject();
          }));
      });
    },

    setUploadedFile(uploadedFile) {
      this.file = uploadedFile;
    },

    showFileUploadModal() {
      EventHelper.triggerModalViewed({
        [description]: 'Open Upload CSV for Import Modal',
        [location]: eventLocations.usersPage,
        [section]: 'Add your first user',
        [text]: 'Select a File',
      });
      this.modalData.CSVOptionsModalVisible = false;
      this.modalData.fileUploadModalVisible = true;
    },

    showImportUserModal() {
      this.modalData.importUsersModalVisible = true;
    },
  },
};
</script>
